import { useState } from "react";
import { ChevronDown, MessageCircle } from "lucide-react";

const faqs = [
  {
    question: "What is the AQT Direct Partners’ Meet?",
    answer:
      "The AQT Direct Partners’ Meet is a strategic industry gathering for investment professionals involved in investor advisory and capital deployment. It focuses on structured investments, capital market readiness, compliance frameworks, and scalable capital collaboration opportunities.",
  },
  {
    question: "Is this event suitable for beginners or students?",
    answer:
      "No. This event is designed for working professionals with prior experience in investment advisory, capital markets, or investor servicing. It is not intended for beginners or students without industry exposure.",
  },
  {
    question: "Is there a fee to attend the event?",
    answer:
      "Yes. Participation is through ticket booking. Ticket pricing and details are available on the Next Happen platform when you click “Book Your Seat.”",
  },
  {
    question: "Will there be networking opportunities at the event?",
    answer:
      "Yes. The event includes structured networking opportunities, including interactions during sessions, as well as during high tea and lunch.",
  },
  {
    question: "How will this event add value to my professional practice?",
    answer:
      "The Partners’ Meet offers practical insights into institutional-grade capital frameworks, helping professionals align with evolving investor expectations and explore long-term collaboration opportunities.",
  },
];

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <section className="relative py-20 md:py-24 px-6 md:px-12 lg:px-24 bg-gradient-to-b from-white via-[#F8FAFC] to-white">
      <div className="absolute inset-x-0 top-40 h-[420px] bg-[#0E6F7E]/5 blur-3xl"></div>
      
      <div className="max-w-5xl mx-auto relative z-10">
        {/* Heading */}
        <div className="text-center mb-16">
          <div className="h-1 w-16 bg-gradient-to-r from-[#D4AF37] to-[#C9A631] mx-auto mb-6"></div>
          <h2 className="text-4xl md:text-5xl font-bold text-[#07162E] mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Key clarifications to help you evaluate alignment and participation.
          </p>
        </div>

        {/* FAQ List */}
        <div className="space-y-4">
          {faqs.map((faq, index) => {
            const isOpen = openIndex === index;

            return (
              <div
                key={index}
                className={`bg-white/80 backdrop-blur-md border border-gray-200/80 rounded-2xl transition-all duration-300
                  ${isOpen
                    ? "shadow-[0_20px_50px_rgba(2,8,23,0.1)] border-gray-300"
                    : "shadow-[0_8px_30px_rgba(2,8,23,0.05)] hover:shadow-[0_15px_40px_rgba(2,8,23,0.08)]"
                  }
                `}
              >
                <button
                  onClick={() =>
                    setOpenIndex(isOpen ? null : index)
                  }
                  className="w-full flex items-center justify-between px-6 md:px-8 py-5 text-left"
                >
                  <span className="text-base md:text-lg font-semibold text-[#07162E]">
                    {faq.question}
                  </span>

                  <ChevronDown
                    className={`h-6 w-6 text-[#0E6F7E] transition-transform duration-300 ${
                      isOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>

                {/* Using a conditional rendering with a wrapper for animation */}
                <div
                  className={`grid transition-all duration-500 ease-in-out ${
                    isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
                  }`}
                >
                  <div className="overflow-hidden">
                    <div className="px-6 md:px-8 pb-6 pt-1 text-gray-600 leading-relaxed border-t border-gray-200/80">
                      <p className="mt-4">{faq.answer}</p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* CTA */}
        <div className="relative mt-20 bg-gradient-to-br from-[#07162E] via-[#0B2A4A] to-[#0E6F7E] rounded-2xl px-6 sm:px-10 py-6 text-center text-white overflow-hidden">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0icmdiYSgyNTUsMjU1LDI1NSwwLjAzKSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] opacity-20"></div>
          <div className="relative z-10">
            <h3 className="text-2xl md:text-3xl font-bold mb-3">
              Need Further Clarification?
            </h3>
            <p className="text-white/80 max-w-xl mx-auto mb-8 text-base md:text-lg">
              Connect with the AQT Direct team to discuss program eligibility,
              participation, and partnership pathways.
            </p>

            <a
              href="https://wa.me/919667773258"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-gradient-to-r from-[#D4AF37] to-[#C9A631] text-[#07162E] font-semibold px-7 py-3.5 rounded-full
                         hover:shadow-[0_0_40px_rgba(212,175,55,0.45)] transition-all duration-300 shadow-lg hover:-translate-y-1"
            >
              <MessageCircle className="h-5 w-5" />
              Contact Us on WhatsApp
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
